import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Recycle, TrendingUp, AlertTriangle, Download, RefreshCw, Loader2, Calendar, Filter, Factory, Flame, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

function MaterialRecovery() {
  const { API } = useContext(AppContext);
  const [dailyData, setDailyData] = useState([]);
  const [furnaceData, setFurnaceData] = useState([]);
  const [totals, setTotals] = useState({
    totalInput: 0,
    totalOutput: 0,
    totalDross: 0,
    totalBurningLoss: 0,
    avgRecoveryPercent: 0,
    avgDrossPercent: 0,
    avgLossPercent: 0,
    totalEntries: 0,
    furnaceCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date range filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [furnaceFilter, setFurnaceFilter] = useState('all');

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={12} className="ml-1 inline opacity-40" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={12} className="ml-1 inline text-green-600" />
      : <ArrowDown size={12} className="ml-1 inline text-green-600" />;
  };

  const filteredFurnaceData = useMemo(() => {
    if (furnaceFilter === 'all') return furnaceData;
    return furnaceData.filter(item => {
      const name = (item.furnace || '').toUpperCase().trim();
      if (furnaceFilter === 'big') {
        return /^C\d+$/i.test(name);
      }
      if (furnaceFilter === 'small') {
        return /^SM\d+$/i.test(name);
      }
      return true;
    });
  }, [furnaceData, furnaceFilter]);

  const filteredTotals = useMemo(() => {
    if (furnaceFilter === 'all') return totals;
    const data = filteredFurnaceData;
    const totalInput = data.reduce((s, d) => s + (d.totalInput || 0), 0);
    const totalOutput = data.reduce((s, d) => s + (d.totalOutput || 0), 0);
    const totalDross = data.reduce((s, d) => s + (d.dross || 0), 0);
    const totalBurningLoss = totalInput - totalOutput - totalDross;
    const totalEntries = data.reduce((s, d) => s + (d.entries || 0), 0);
    return {
      totalInput,
      totalOutput,
      totalDross,
      totalBurningLoss: Math.max(0, totalBurningLoss),
      avgRecoveryPercent: totalInput > 0 ? (totalOutput / totalInput) * 100 : 0,
      avgDrossPercent: totalInput > 0 ? (totalDross / totalInput) * 100 : 0,
      avgLossPercent: totalInput > 0 ? (Math.max(0, totalBurningLoss) / totalInput) * 100 : 0,
      totalEntries,
      furnaceCount: data.length
    };
  }, [filteredFurnaceData, furnaceFilter, totals]);

  const sortedFurnaceData = useMemo(() => {
    if (!sortConfig.key) return filteredFurnaceData;
    return [...filteredFurnaceData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredFurnaceData, sortConfig]);

  useEffect(() => {
    fetchRecoveryData();
  }, []);

  const fetchRecoveryData = async (start = '', end = '') => {
    setLoading(true);
    setError(null);
    try {
      // Build query params for date range
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const response = await axios.get(`${API}/production/material-recovery${queryString}`);
      
      setDailyData(response.data.dailyData || []);
      setFurnaceData(response.data.furnaceData || []);
      setTotals(response.data.totals || {
        totalInput: 0,
        totalOutput: 0,
        totalDross: 0,
        totalBurningLoss: 0,
        avgRecoveryPercent: 0,
        avgDrossPercent: 0,
        avgLossPercent: 0,
        totalEntries: 0,
        furnaceCount: 0
      });
      setFilterApplied(!!start || !!end);
    } catch (err) {
      console.error('Error fetching recovery data:', err);
      setError('Failed to load material recovery data');
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = () => {
    fetchRecoveryData(startDate, endDate);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchRecoveryData('', '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-green-500 mr-2" size={24} />
        <span className="text-gray-600">Loading material recovery data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="material-recovery-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Material Recovery</h1>
          <p className="text-gray-500 mt-1">Auto-fetched from Production Report - Track recovery rates by furnace</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => fetchRecoveryData(startDate, endDate)}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2">
            <Download size={16} />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="text-gray-500" size={18} />
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <button
            onClick={applyDateFilter}
            disabled={!startDate && !endDate}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Filter size={16} />
            Apply Filter
          </button>
          {filterApplied && (
            <button
              onClick={clearDateFilter}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
            >
              Clear Filter
            </button>
          )}
          {filterApplied && (
            <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
              Showing: {startDate || 'Start'} → {endDate || 'End'}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">
          {error}
        </div>
      )}

      {/* Furnace Size Filter */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="furnace-filter-section">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Factory className="text-gray-500" size={18} />
            <span className="text-sm font-medium text-gray-700">Furnace Type:</span>
          </div>
          {[
            { value: 'all', label: 'All Furnaces' },
            { value: 'big', label: 'Big (C1-C15)' },
            { value: 'small', label: 'Small (SM1-SM7)' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFurnaceFilter(opt.value)}
              data-testid={`furnace-filter-${opt.value}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                furnaceFilter === opt.value
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {furnaceFilter !== 'all' && (
            <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
              Showing: {furnaceFilter === 'big' ? 'Big Furnaces (C1-C15)' : 'Small Furnaces (SM1-SM7)'}
            </span>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Avg Recovery Rate</p>
              <p className="text-3xl font-bold text-green-700">{filteredTotals.avgRecoveryPercent.toFixed(1)}%</p>
              <p className="text-xs text-green-500 mt-1">Output / Input</p>
            </div>
            <div className="p-3 bg-green-500 rounded-xl">
              <Recycle className="text-white" size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Avg Dross</p>
              <p className="text-3xl font-bold text-orange-700">{filteredTotals.avgDrossPercent.toFixed(1)}%</p>
              <p className="text-xs text-orange-500 mt-1">{filteredTotals.totalDross.toLocaleString()} kg total</p>
            </div>
            <div className="p-3 bg-orange-500 rounded-xl">
              <AlertTriangle className="text-white" size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Avg Burning Loss</p>
              <p className="text-3xl font-bold text-red-700">{filteredTotals.avgLossPercent.toFixed(1)}%</p>
              <p className="text-xs text-red-500 mt-1">{filteredTotals.totalBurningLoss.toLocaleString()} kg total</p>
            </div>
            <div className="p-3 bg-red-500 rounded-xl">
              <Flame className="text-white" size={28} />
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Production</p>
              <p className="text-3xl font-bold text-blue-700">{(filteredTotals.totalOutput / 1000).toFixed(1)}T</p>
              <p className="text-xs text-blue-500 mt-1">From {(filteredTotals.totalInput / 1000).toFixed(1)}T input</p>
            </div>
            <div className="p-3 bg-blue-500 rounded-xl">
              <TrendingUp className="text-white" size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Recovery Chart */}
      {dailyData.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Daily Recovery Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
                formatter={(value, name) => {
                  const labels = {
                    recoveryPercent: 'Recovery',
                    drossPercent: 'Dross',
                    lossPercent: 'Burning Loss'
                  };
                  return [`${value.toFixed(1)}%`, labels[name] || name];
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="recoveryPercent" name="Recovery %" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
              <Area type="monotone" dataKey="drossPercent" name="Dross %" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.4} />
              <Area type="monotone" dataKey="lossPercent" name="Loss %" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Furnace-wise Recovery Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-900">Recovery by Furnace (from Production Data)</h3>
        </div>
        <div className="overflow-x-auto">
          {furnaceData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Recycle className="mx-auto mb-3 text-gray-300" size={48} />
              <p className="font-medium">No production data found</p>
              <p className="text-sm">Recovery data will appear here once production entries are recorded</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                <tr>
                  <th onClick={() => handleSort('furnace')} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Furnace <SortIcon columnKey="furnace" /></th>
                  <th onClick={() => handleSort('totalInput')} className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Input (kg) <SortIcon columnKey="totalInput" /></th>
                  <th onClick={() => handleSort('totalOutput')} className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Output (kg) <SortIcon columnKey="totalOutput" /></th>
                  <th onClick={() => handleSort('recoveryPercent')} className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Recovery % <SortIcon columnKey="recoveryPercent" /></th>
                  <th onClick={() => handleSort('dross')} className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Dross (kg) <SortIcon columnKey="dross" /></th>
                  <th onClick={() => handleSort('drossPercent')} className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Dross % <SortIcon columnKey="drossPercent" /></th>
                  <th onClick={() => handleSort('entries')} className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none">Entries <SortIcon columnKey="entries" /></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedFurnaceData.map((item) => (
                  <tr key={item.furnace} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-green-100 rounded">
                          <Factory className="text-green-600" size={16} />
                        </div>
                        <span className="font-medium text-gray-900">{item.furnace}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-gray-600">
                      {item.totalInput.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-gray-900">
                      {item.totalOutput.toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${item.recoveryPercent >= 90 ? 'bg-green-500' : item.recoveryPercent >= 80 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(item.recoveryPercent, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-bold ${item.recoveryPercent >= 90 ? 'text-green-600' : item.recoveryPercent >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {item.recoveryPercent.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-orange-600 font-medium">
                      {item.dross.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-600">
                      {item.drossPercent.toFixed(1)}%
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        {item.entries}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-700">{filteredTotals.totalInput.toLocaleString()} kg</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{filteredTotals.totalOutput.toLocaleString()} kg</td>
                  <td className="px-4 py-3 text-center font-bold text-green-600">{filteredTotals.avgRecoveryPercent.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right font-medium text-orange-600">{filteredTotals.totalDross.toLocaleString()} kg</td>
                  <td className="px-4 py-3 text-center font-medium text-gray-700">{filteredTotals.avgDrossPercent.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-center text-gray-500">{filteredTotals.totalEntries}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <p className="text-green-800 text-sm">
          <strong>Note:</strong> Material recovery data is automatically calculated from Production Data Entry records. 
          Recovery Rate = (Total Output / Total Input) × 100%. 
          Higher recovery rates indicate better material utilization efficiency.
        </p>
      </div>
    </div>
  );
}

export default MaterialRecovery;
